import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import styled from 'styled-components';

import { PATHS } from 'router/config';
import { getFontFamily } from 'services/FontService';

interface IZoomTab {
  path: string;
  label: string;
}

/**
 * Query-string keys that make up the *shared* Zoom filter (see useZoomFilters).
 * They are carried across tab navigation so the selected group + date range
 * stay applied; page-private params (status, orphanOnly, …) are intentionally
 * dropped on tab switch.
 */
const SHARED_FILTER_KEYS = ['from', 'to', 'group', 'showDeleted'] as const;

const getZoomTabs = (t: TFunction): IZoomTab[] => [
  { path: PATHS.zoomDashboard, label: `${t('pages.zoom.tabs.dashboard')}` },
  { path: PATHS.zoomMatching, label: `${t('pages.zoom.tabs.matching')}` },
  { path: PATHS.zoomLessons, label: `${t('pages.zoom.tabs.lessons')}` },
  { path: PATHS.zoomRecordings, label: `${t('pages.zoom.tabs.recordings')}` },
  { path: PATHS.zoomRecent, label: `${t('pages.zoom.tabs.recent')}` },
  { path: PATHS.zoomAccounts, label: `${t('pages.zoom.tabs.accounts')}` },
  { path: PATHS.zoomSchedule, label: `${t('pages.zoom.tabs.schedule')}` },
  { path: PATHS.zoomAudit, label: `${t('pages.zoom.tabs.audit')}` },
];

export const ZoomTabs = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const sharedSearch = React.useMemo(() => {
    const parsed = queryString.parse(location.search);
    const shared: Record<string, unknown> = {};
    SHARED_FILTER_KEYS.forEach((key) => {
      if (parsed[key] != null && parsed[key] !== '') {
        shared[key] = parsed[key];
      }
    });
    return queryString.stringify(shared);
  }, [location.search]);

  return (
    <TabsBar>
      {getZoomTabs(t).map((tab) => (
        <TabLink
          key={tab.path}
          to={{ pathname: tab.path, search: sharedSearch }}
          activeClassName="active"
        >
          {tab.label}
        </TabLink>
      ))}
    </TabsBar>
  );
};

const TabsBar = styled.nav`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-top: 24px;
`;

const TabLink = styled(NavLink)`
  padding: 12px 0;
  font-family: ${getFontFamily('regular')};
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #111827;
  }

  &.active {
    color: #111827;
    border-bottom-color: #2563eb;
    font-family: ${getFontFamily('bold')};
  }
`;
